'use client';
import { motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import { cardItemVariants, cardsContainerVariants } from '@/app/components/shared/motion-variants';
import { useStore } from '@/app/providers/store';
import type { ClawPassProduct } from '@/app/types';
import { cn } from '@/app/utils';
import { GlobalLoader } from '../GlobalLoader';
import { ProductEmptyState } from '../ProductEmptyState';
import { type ClawPassProductType, PassCard } from './PassCard';
import { SubscriptionButton } from './SubscriptionButton';
import { groupClawPassProducts } from './utils';

interface ClawPassContentProps {
  products: ClawPassProduct[];
  loadingProducts?: boolean;
  className?: string;
}

export const ClawPassContent = ({ products, loadingProducts, className }: ClawPassContentProps) => {
  const clawPass = useStore((s) => s.clawPass);

  const groups = useMemo(() => groupClawPassProducts(products), [products]);

  // 全局是否存在两种 product_type（任一档位有即视为可切换）
  const hasBundle = useMemo(() => products.some((p) => p.product_type === 'claw_pass_bundle'), [products]);
  const hasPass = useMemo(() => products.some((p) => p.product_type === 'claw_pass'), [products]);

  // 默认选中策略：跟随用户当前持有的 product_type；否则优先 bundle 后 pass。
  // 用 derived state，避免 clawPass 异步到达时初始值锁死。
  const defaultType = useMemo<ClawPassProductType>(() => {
    if (clawPass?.product_type === 'claw_pass_bundle' && hasBundle) return 'claw_pass_bundle';
    if (clawPass?.product_type === 'claw_pass' && hasPass) return 'claw_pass';
    if (hasBundle) return 'claw_pass_bundle';
    return 'claw_pass';
  }, [clawPass?.product_type, hasBundle, hasPass]);

  // 用户主动切换的覆盖值；未切换时跟随 defaultType。整体切换：一处切换，所有卡片同步。
  const [selectedType, setSelectedType] = useState<ClawPassProductType | null>(null);
  const activeType = selectedType ?? defaultType;

  const renderSubscriptionButton = (product: ClawPassProduct, quantity: number) => {
    return <SubscriptionButton product={product} quantity={quantity} />;
  };

  if (loadingProducts && products.length === 0)
    return (
      <div className="flex min-h-[400px] w-full items-center justify-center">
        <GlobalLoader className="static translate-x-0 translate-y-0" />
      </div>
    );
  if (products.length === 0) return <ProductEmptyState className="mt-10" />;

  return (
    <motion.div
      className={cn('flex items-stretch justify-center gap-4 pt-6 sm:flex-col sm:px-4', className)}
      initial="hidden"
      animate="visible"
      variants={cardsContainerVariants}
    >
      {groups.map((group) => (
        <motion.div key={group.key} variants={cardItemVariants}>
          <PassCard
            productGroup={group.products}
            renderSubscriptionButton={renderSubscriptionButton}
            activeType={activeType}
            onTypeChange={setSelectedType}
          />
        </motion.div>
      ))}
    </motion.div>
  );
};
